/**
 * OverlapConflictDialog — non-blocking review of overlap warnings after a save.
 *
 * Overlapping closed entries are first-class behaviour (parallel work is
 * documented): by the time this dialog opens the save has ALREADY succeeded,
 * so there is nothing to cancel. Per overlapping entry the owner can:
 *
 *  - Accept overlap: persist an acknowledgement — this pair never warns again.
 *  - Trim previous: clamp the OTHER entry away from this one via the ordinary
 *    update endpoint (end pulled back to this start when it began earlier,
 *    start pushed out to this end otherwise).
 *  - Trim this: the mirror clamp applied to the just-saved entry.
 *  - Dismiss: close with no action — the entry already exists.
 *
 * A trim that cannot produce a valid interval (one entry fully inside the
 * other on that side) is disabled rather than sent to fail server-side.
 *
 * 'Split around the overlap' is deliberately not offered: with only
 * create/update endpoints it would be a client-side create+update
 * orchestration with partial-failure states — noted as a follow-up instead.
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import axios from 'axios';

import { getApiErrorMessage } from '../utils/apiError';
import { useLayoutTokens } from '../hooks/useLayoutTokens';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export interface OverlapEntrySummary {
  id: number;
  start_time: string;
  end_time?: string | null;
  description?: string | null;
  project?: string | null;
  task?: string | null;
  acknowledged?: boolean;
}

export interface OverlapConflictEntry {
  id: number;
  start_time: string;
  end_time?: string | null;
  description?: string | null;
}

interface TrimPlan {
  targetId: number;
  payload: { start_time?: string; end_time?: string };
  valid: boolean;
}

interface OverlapConflictDialogProps {
  open: boolean;
  /** The just-saved entry the warnings are about. */
  entry: OverlapConflictEntry | null;
  /** Unacknowledged overlaps from the save response. */
  overlaps: OverlapEntrySummary[];
  onClose: () => void;
  /** Called once, on close, if any accept/trim actually changed data. */
  onResolved: () => void;
}

const parseMs = (iso?: string | null): number =>
  iso ? new Date(iso).getTime() : Number.NaN;

const fmtRange = (start: string, end?: string | null): string => {
  const from = new Date(start);
  const to = end ? new Date(end) : null;
  if (Number.isNaN(from.getTime())) return '-';
  return `${from.toLocaleString()} → ${
    to && !Number.isNaN(to.getTime()) ? to.toLocaleString() : 'running'
  }`;
};

/** Clamp the OTHER entry away from the saved one. */
const planTrimOther = (
  entry: OverlapConflictEntry,
  other: OverlapEntrySummary,
): TrimPlan | null => {
  const entryStart = parseMs(entry.start_time);
  const entryEnd = parseMs(entry.end_time);
  const otherStart = parseMs(other.start_time);
  const otherEnd = parseMs(other.end_time);
  if ([entryStart, entryEnd, otherStart, otherEnd].some(Number.isNaN)) return null;
  if (otherStart < entryStart) {
    // The other entry began earlier: pull its end back to this start.
    return {
      targetId: other.id,
      payload: { end_time: entry.start_time },
      valid: true,
    };
  }
  // The other entry starts inside this one: push its start to this end. Only
  // valid when it also outlasts this entry — otherwise it is fully contained.
  return {
    targetId: other.id,
    payload: { start_time: entry.end_time as string },
    valid: entryEnd <= otherEnd,
  };
};

/** The mirror clamp, applied to the just-saved entry. */
const planTrimThis = (
  entry: OverlapConflictEntry,
  other: OverlapEntrySummary,
): TrimPlan | null => {
  const entryStart = parseMs(entry.start_time);
  const entryEnd = parseMs(entry.end_time);
  const otherStart = parseMs(other.start_time);
  const otherEnd = parseMs(other.end_time);
  if ([entryStart, entryEnd, otherStart, otherEnd].some(Number.isNaN)) return null;
  if (otherStart < entryStart) {
    // Other began earlier: this entry starts where the other stops.
    return {
      targetId: entry.id,
      payload: { start_time: other.end_time as string },
      valid: otherEnd <= entryEnd,
    };
  }
  // Other starts inside this entry: this entry stops where the other begins.
  return {
    targetId: entry.id,
    payload: { end_time: other.start_time },
    valid: true,
  };
};

const OverlapConflictDialog: React.FC<OverlapConflictDialogProps> = ({
  open,
  entry,
  overlaps,
  onClose,
  onResolved,
}) => {
  const { dialogContentPaddingSx } = useLayoutTokens();
  const [remaining, setRemaining] = useState<OverlapEntrySummary[]>([]);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const mutatedRef = useRef(false);

  useEffect(() => {
    if (open) {
      setRemaining(overlaps);
      setError(null);
      mutatedRef.current = false;
    }
  }, [open, entry, overlaps]);

  const closeAndMaybeRefresh = () => {
    const mutated = mutatedRef.current;
    onClose();
    if (mutated) onResolved();
  };

  const settleRow = (otherId: number) => {
    const next = remaining.filter((item) => item.id !== otherId);
    setRemaining(next);
    if (next.length === 0) closeAndMaybeRefresh();
  };

  const handleAccept = async (other: OverlapEntrySummary) => {
    if (!entry) return;
    try {
      setBusyKey(`accept-${other.id}`);
      await axios.post(`${API_BASE_URL}/api/time-entries/overlaps/acknowledge`, {
        entry_a_id: entry.id,
        entry_b_id: other.id,
      });
      mutatedRef.current = true;
      settleRow(other.id);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to acknowledge overlap'));
    } finally {
      setBusyKey(null);
    }
  };

  const handleTrim = async (other: OverlapEntrySummary, plan: TrimPlan | null, key: string) => {
    if (!plan || !plan.valid) return;
    try {
      setBusyKey(key);
      await axios.put(`${API_BASE_URL}/api/time-entries/${plan.targetId}`, plan.payload);
      mutatedRef.current = true;
      settleRow(other.id);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to trim time entry'));
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => (busyKey ? undefined : closeAndMaybeRefresh())}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>Overlapping time entries</DialogTitle>
      <DialogContent sx={dialogContentPaddingSx}>
        <Alert severity="info" sx={{ mb: 2 }}>
          Saved. Overlaps are allowed — nothing was blocked. This entry overlaps{' '}
          {remaining.length === 1 ? 'an existing entry' : `${remaining.length} existing entries`};
          accept each overlap to stop future warnings for that pair, or trim one side.
        </Alert>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {entry && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            {`Just saved: ${entry.description || 'Untitled entry'} · ${fmtRange(
              entry.start_time,
              entry.end_time,
            )}`}
          </Typography>
        )}
        <Stack spacing={1.5}>
          {entry &&
            remaining.map((other) => {
              const trimOther = planTrimOther(entry, other);
              const trimThis = planTrimThis(entry, other);
              const busy = busyKey != null;
              // A repeated list row inside a scrollable sm dialog, not a page
              // panel: panelPaddingSx would grow every row and push the list
              // past the fold. Keeps its own tight literal.
              return (
                <Paper key={other.id} variant="outlined" sx={{ p: 1.5 }}>
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="body2">
                      {other.description || 'Untitled entry'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {[
                        fmtRange(other.start_time, other.end_time),
                        [other.project, other.task].filter(Boolean).join(' · '),
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={busy}
                      onClick={() => handleAccept(other)}
                    >
                      {busyKey === `accept-${other.id}` ? (
                        <CircularProgress size={16} />
                      ) : (
                        'Accept overlap'
                      )}
                    </Button>
                    <Button
                      size="small"
                      disabled={busy || !trimOther || !trimOther.valid}
                      onClick={() => handleTrim(other, trimOther, `trim-other-${other.id}`)}
                    >
                      Trim previous
                    </Button>
                    <Button
                      size="small"
                      disabled={busy || !trimThis || !trimThis.valid}
                      onClick={() => handleTrim(other, trimThis, `trim-this-${other.id}`)}
                    >
                      Trim this
                    </Button>
                  </Stack>
                </Paper>
              );
            })}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeAndMaybeRefresh} disabled={busyKey != null}>
          Dismiss
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OverlapConflictDialog;
